import { OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';

import { I18nTranslator } from '../../../../i18n/translate';
import { AROrder, AROrderItem } from '../../../../models/agrorendement-common.model';
import { ToolStatus } from '../../../../models/parcel.model';
import { ARGenericToolService, OrderSummary } from '../../../../services/agrorendement-common.service';
import { ModalService } from '../../../../services/modal.service';
import { ParcelService } from '../../../../services/parcel.service';
import { showErrorMessage } from '../../../../utils/utils';

export abstract class AROrderEditComponent<O extends AROrder<any, OI>, OI extends AROrderItem<any, any>>
  implements OnInit, OnDestroy
{
  sub: any;

  edited = false;
  readOnly = false;
  agreeTos = false;
  hasChangedOrderItemList = false;

  orderId: number;
  order: O;
  orderItems: OI[];
  orderSummary: OrderSummary = { area: 0, numberOfParcels: 0 };

  // a redefinir dans les sous classes
  routeActionIndex: number;
  specyUrlPart: string[];
  specyString: string;
  specyIconClass: string;
  specyOrderCode: string;
  toolStatusCode: string;

  // dans le constructeur de la sous-class (injectes)
  protected i18nTranslator: I18nTranslator;
  protected service: ARGenericToolService<any, O, OI>;
  protected parcelService: ParcelService;
  protected modal: ModalService;
  protected router: Router;
  protected route: ActivatedRoute;

  // a overrider
  abstract buildNew(): O;
  abstract remoteGetOrderItems(orderItemIds: Array<number>): Observable<OI[]>;
  abstract remoteGetOrderItem(orderItemId: number): Observable<OI>;

  goBack(isCancel = false) {
    if (isCancel && !this.edited && !this.readOnly) {
      // annulation + vient de la liste des parcelles => retour a cette page
      this.router.navigate(['/orders'].concat(this.specyUrlPart).concat(['parcels']));
    } else {
      this.router.navigate(['/orders'].concat(this.specyUrlPart).concat(['orders']));
    }
    return false;
  }

  save() {
    if (this.orderItems.length === 0) {
      this.modal.confirm(this.i18nTranslator.translate('ar.order.edit.prompt.delete')).then((ok: boolean) => {
        if (ok) {
          this.service.deleteOrder(this.order).subscribe(
            (order) => {
              this.goBack();
            },
            (error) => this.handleSaveError(error),
          );
        }
      });
    } else {
      this.service.saveOrder(this.order).subscribe(
        (order) => {
          if (!this.edited) {
            this.modal.alert(this.i18nTranslator.translate('ar.order.edit.prompt.confirmation'));
          }
          this.goBack();
        },
        (error) => this.handleSaveError(error),
      );
    }
  }

  ngOnInit() {
    this.sub = this.route.params.subscribe((params) => {
      if (this.route.snapshot.url[this.routeActionIndex].path === 'new') {
        this.edited = false;
        this.order = this.buildNew();
        if (params['parcelId']) {
          // fonctionne aussi si un seul
          const parcelIds = params['parcelId'].split('-').map((x: string) => +x);
          this.getOrderItemsForNew(parcelIds);
        }
      } else if (this.route.snapshot.url[this.routeActionIndex].path === 'edit') {
        this.orderId = +params['orderId'];
        this.edited = true;
        this.service.getOrder(this.orderId).subscribe(
          (order) => {
            this.order = order;
            this.orderItems = order.orderItems;

            this.computeStats();
          },
          (e) => this.handleGetError(e, this.i18nTranslator.translate('ar.order.edit.error.load')),
        );
      } else if (this.route.snapshot.url[this.routeActionIndex].path === 'view') {
        this.orderId = +params['orderId'];
        this.readOnly = true;
        // FIXME traiter les erreurs ; afficher un message
        this.service.getOrder(this.orderId).subscribe(
          (order) => {
            this.order = order;
            this.orderItems = order.orderItems;
            this.computeStats();
          },
          (e) => this.handleGetError(e, this.i18nTranslator.translate('ar.order.edit.error.load')),
        );
      }
    });
  }

  getOrderItemsForNew(parcelIds: number[]) {
    if (!parcelIds || parcelIds.length === 0) {
      this.router.navigate(['/orders'].concat(this.specyUrlPart).concat(['orders']));
      return;
    }

    this.modal.alert(this.i18nTranslator.translate('ar.order.edit.wait')).then(() => {
      // dialog dismissed
    });

    this.parcelService.getParcelStatuses(parcelIds).subscribe((x) => {
      const orderItemIds: number[] = [];
      let canOrder = true;
      const causes = new Set<string>();
      for (const p of x) {
        const results: Array<string> = [];
        if (p.toolStatuses && (<any>p.toolStatuses)[this.toolStatusCode]) {
          const toolStatus = <ToolStatus>(<any>p.toolStatuses)[this.toolStatusCode];
          if (!toolStatus.hasOrder && toolStatus.canBeOrdered && toolStatus.orderItemId) {
            orderItemIds.push(toolStatus.orderItemId);
          } else {
            canOrder = false;
            if (toolStatus.hasOrder) {
              causes.add('hasOrder');
            }
            if (!toolStatus.canBeOrdered || !toolStatus.orderItemId) {
              causes.add('orderItem');
            }
          }
        }
      }
      if (!canOrder) {
        // premier check basique
        let msg = '';
        if (causes.has('hasOrder')) {
          msg = this.i18nTranslator.translate('ar.order.edit.error.hasOrder');
        } else if (causes.has('orderItem')) {
          msg = this.i18nTranslator.translate('ar.order.edit.error.orderItem');
        }
        showErrorMessage(this.modal, msg).then((r) => {
          this.router.navigate(['/orders'].concat(this.specyUrlPart).concat(['parcels']));
        });
      } else {
        this.remoteGetOrderItems(orderItemIds).subscribe(
          (orderItems) => {
            // 2eme check => le critere complet d'orderabilite est verifie dans la requete remoteGetOrderItems
            if (orderItems.length !== orderItemIds.length) {
              showErrorMessage(this.modal, this.i18nTranslator.translate('ar.order.edit.error.orderItem')).then((r) => {
                this.router.navigate(['/orders'].concat(this.specyUrlPart).concat(['parcels']));
              });
            }
            this.handleOrderItems(orderItems);
          },
          (error) => {
            this.handleGetError(
              error,
              this.i18nTranslator.translate('ar.order.edit.error.loadInfoParcelles', this.specyString),
            );
          },
        );
      }
    });
  }

  handleOrderItems(orderItems: OI[]) {
    this.orderItems = orderItems;
    this.order.orderItems = orderItems;

    // on autorise la validation de la commande
    this.hasChangedOrderItemList = true;

    this.computeStats();
  }

  deleteOrderItem(orderItem: OI, event: any) {
    this.modal.confirm(this.i18nTranslator.translate('ar.order.edit.delete.confirm')).then((ok: boolean) => {
      if (ok) {
        const index = this.orderItems.findIndex((x) => x.id === orderItem.id);
        this.orderItems.splice(index, 1);
        this.hasChangedOrderItemList = true;

        this.computeStats();
      }
    });
  }

  handleSaveError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.order.edit.unauthorized'));
    } else {
      // FIXME detailler les erreurs du formulaire
      // FIXME definir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('edit.error.genericm'));
    }
  }

  handleGetError(error: any, msg: string) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.order.edit.unauthorized.view')).then((r) => {
        this.router.navigate(['/orders'].concat(this.specyUrlPart).concat(['orders']));
      });
    } else {
      console.error(error);
      showErrorMessage(this.modal, msg).then((r) => {
        this.router.navigate(['/orders'].concat(this.specyUrlPart).concat(['orders']));
      });
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  computeStats() {
    let sumArea = 0.0;
    for (const orderItem of this.orderItems) {
      sumArea += orderItem.parcel.area;
    }
    this.orderSummary.area = sumArea;
    this.orderSummary.numberOfParcels = this.orderItems.length;
  }
}
